import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';

import { GithubOrgsApi, GithubOrgsResponse } from './GithubOrgsApi';

export class GithubOrgsClient implements GithubOrgsApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  async getOrgs(
    host: string,
    refresh: boolean = false,
  ): Promise<GithubOrgsResponse> {
    try {
      const baseUrl = await this.discoveryApi.getBaseUrl('github-orgs');
      const url = `${baseUrl}/orgs/${host}${refresh ? '?refresh=true' : ''}`;

      const response = await this.fetchApi.fetch(url);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(
          errorData.message ||
            `Failed to fetch GitHub organizations: ${response.statusText}`,
        ) as Error & {
          status?: number;
          statusCode?: number;
          response?: Response;
        };
        error.status = errorData.statusCode || response.status;
        error.statusCode = errorData.statusCode || response.status;
        error.response = response;
        throw error;
      }

      return (await response.json()) as GithubOrgsResponse;
    } catch (error: unknown) {
      const err = error as Error & { status?: number; response?: Response };
      const newError = new Error(
        `Failed to fetch GitHub organizations: ${err.message}`,
      ) as Error & { status?: number; response?: Response };
      if (err.status) {
        newError.status = err.status;
      }
      if (err.response) {
        newError.response = err.response;
      }
      throw newError;
    }
  }

  async repoExists(host: string, org: string, repo: string): Promise<boolean> {
    const baseUrl = await this.discoveryApi.getBaseUrl('github-orgs');
    const url = `${baseUrl}/repos/${encodeURIComponent(
      host,
    )}/${encodeURIComponent(org)}/${encodeURIComponent(repo)}`;

    const response = await this.fetchApi.fetch(url);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(
        `Failed to check GitHub repository: ${
          errorData.message || response.statusText
        }`,
      ) as Error & { status?: number };
      error.status = errorData.statusCode || response.status;
      throw error;
    }

    const data = (await response.json()) as { exists?: boolean };
    return Boolean(data.exists);
  }
}
